// Hamburg districts grid — shows arrival times for 20 neighborhoods
import { MapPin, Zap, Clock } from "lucide-react";
import { useState } from "react";
import { useLang } from "../contexts/LangContext";
import { HAMBURG_DISTRICTS } from "../data/hamburgDistricts";

export default function HamburgDistricts() {
  const { t } = useLang();
  const ct = t.districts;
  const [filter, setFilter] = useState("all");

  const filtered = HAMBURG_DISTRICTS.filter((d) => {
    if (filter === "express") return d.express_min <= 22;
    if (filter === "hamburg") return d.region === "Hamburg";
    if (filter === "umland") return d.region === "Umland";
    if (filter === "norddeutschland") return d.region === "Norddeutschland";
    return true;
  });

  return (
    <section className="py-16 lg:py-24 bg-white dark:bg-[#0F1012]" data-testid="hamburg-districts-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#FF5A00] mb-3">{ct.eyebrow}</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mb-3">{ct.title}</h2>
          <p className="text-sm sm:text-base text-neutral-600 dark:text-neutral-400 max-w-2xl mx-auto leading-relaxed">
            {ct.subtitle}
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {[
            { key: "all",             label: ct.filter_all },
            { key: "hamburg",         label: ct.filter_hamburg || "Hamburg" },
            { key: "umland",          label: ct.filter_umland || "Umland" },
            { key: "norddeutschland", label: ct.filter_norddeutschland || "Norddeutschland" },
            { key: "express",         label: ct.filter_express, icon: Zap, accent: true },
          ].map((btn) => (
            <button
              key={btn.key}
              type="button"
              onClick={() => setFilter(btn.key)}
              className={`px-4 py-2 text-xs font-bold tracking-wider uppercase border transition-colors inline-flex items-center gap-1.5 ${
                filter === btn.key
                  ? btn.accent
                    ? "bg-[#FF5A00] text-white border-[#FF5A00]"
                    : "bg-[#090A0B] text-white border-[#090A0B]"
                  : "bg-white dark:bg-[#0F1012] text-neutral-700 dark:text-neutral-300 border-neutral-300 dark:border-neutral-700 hover:border-[#FF5A00]"
              }`}
              data-testid={`districts-filter-${btn.key}`}
            >
              {btn.icon && <btn.icon size={11} />}
              {btn.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {filtered.map((d) => (
            <div
              key={d.name}
              className="border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#0A0B0D] p-4 hover:border-[#FF5A00] transition-colors"
              data-testid={`district-${d.name.split(" ")[0].toLowerCase().replace(/[^a-z]/g, "")}`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="font-bold text-sm leading-tight">{d.name}</div>
                {d.express_min <= 22 && (
                  <Zap size={11} className="text-[#FF5A00] flex-shrink-0 mt-0.5" />
                )}
              </div>
              <div className="text-[10px] font-mono text-neutral-400 mb-3">{d.plz_range}</div>
              <div className="flex items-center justify-between gap-1.5 text-xs">
                <div className="flex items-center gap-1.5">
                  <Clock size={11} className="text-neutral-400" />
                  <span className="font-black tracking-tight tabular-nums" style={{ color: d.color }}>
                    ~{d.express_min} min
                  </span>
                </div>
                <span className="text-[9px] font-bold uppercase tracking-widest text-neutral-400">
                  {d.region}
                </span>
              </div>
            </div>
          ))}
        </div>

        <p className="text-center text-[11px] text-neutral-500 mt-8">
          <MapPin size={11} className="inline-block me-1 -mt-0.5" />
          {ct.footnote}
        </p>
      </div>
    </section>
  );
}
